// Shared group color palette + dark/light mode resolver.
// Used by Device Groups, Geofence Groups, and any future Group features (People, Fleet, etc.).
// Source of truth — DO NOT duplicate this palette in individual components.

export const DEFAULT_GROUP_COLOR = '#475569'; // Slate-600 — neutral default for legacy entries

export const GROUP_PRESET_COLORS = [
  { value: '#2563eb', label: 'Blue' },
  { value: '#dc2626', label: 'Red' },
  { value: '#16a34a', label: 'Green' },
  { value: '#F59E0B', label: 'Amber' },
  { value: '#eab308', label: 'Yellow' },
  { value: '#ea580c', label: 'Orange' },
  { value: '#0d9488', label: 'Teal' },
  { value: '#4f46e5', label: 'Indigo' },
  { value: '#9333ea', label: 'Purple' },
  { value: '#be185d', label: 'Pink' },
  { value: '#8B6914', label: 'Brown' },
  { value: '#475569', label: 'Slate' },
];

// Compute the relative luminance of a hex color (0-1 range).
// Used to decide if the color is too dark to be readable on a dark background.
const relativeLuminance = (hex) => {
  if (!hex || typeof hex !== 'string') return 0.5;
  const clean = hex.replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16) / 255;
  const g = parseInt(clean.substring(2, 4), 16) / 255;
  const b = parseInt(clean.substring(4, 6), 16) / 255;
  // sRGB luminance approximation
  const lin = (c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
};

// Lighten / darken a hex by a percentage (-100 to +100).
const adjustLightness = (hex, pct) => {
  if (!hex || typeof hex !== 'string') return hex;
  const clean = hex.replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  const adjust = (c) => {
    if (pct >= 0) return Math.min(255, Math.round(c + (255 - c) * (pct / 100)));
    return Math.max(0, Math.round(c * (1 + pct / 100)));
  };
  const toHex = (n) => n.toString(16).padStart(2, '0');
  return `#${toHex(adjust(r))}${toHex(adjust(g))}${toHex(adjust(b))}`;
};

// Resolve the readable text color for a group name in the current theme.
// - Light mode: returns the color as-is (all 12 presets are dark enough).
// - Dark mode: brightens the color if its luminance < 0.35 so it stays readable.
export const getGroupTextColor = (hex, isDark) => {
  if (!hex) return isDark ? '#cbd5e1' : '#475569';
  if (!isDark) return hex;
  const lum = relativeLuminance(hex);
  if (lum < 0.35) {
    // Brighten toward white. The lower the luminance, the more brightening needed.
    const boost = lum < 0.15 ? 55 : 35;
    return adjustLightness(hex, boost);
  }
  return hex;
};

// Detect dark mode by checking the documentElement class.
// Components should re-render on theme change because the theme toggle adds/removes
// the `dark` class on <html>, which triggers any descendant using this util.
export const isDarkMode = () => {
  if (typeof document === 'undefined') return false;
  return document.documentElement.classList.contains('dark');
};

// Convenience: returns the resolved text color using the current document theme.
export const resolveGroupTextColor = (hex) => getGroupTextColor(hex, isDarkMode());
